import {
  Menu,
  MenuButton,
  MenuHeading,
  MenuItem,
  MenuItems,
  MenuSection,
} from "@headlessui/react";
import {
  DEFAULT_DASH_LANGUAGE,
  DashLanguages,
  normalizeDashLanguage,
} from "dash-common";
import { ViewerContext } from "dash-ui";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { FiCheck, FiChevronDown, FiGlobe } from "react-icons/fi";

import {
  MULTILINGUAL_ENABLED,
  PORTAL_LANGUAGE_NAMES,
  portalLanguagePick,
} from "shared/i18n";

const LANGUAGES = Object.values(DashLanguages);

/** Changes the language of this browser only; the account language lives in Settings. */
export function PortalLanguageSwitcher({ className }: { className?: string }) {
  const { t, i18n } = useTranslation(["common"]);
  const { account } = useContext(ViewerContext);

  if (!MULTILINGUAL_ENABLED) return null;

  const current = normalizeDashLanguage(i18n.language) ?? DEFAULT_DASH_LANGUAGE;
  const label = `${t("common:languageSwitcher:label")}: ${
    PORTAL_LANGUAGE_NAMES[current]
  } (${current.toUpperCase()})`;

  return (
    <Menu as="div" className={className}>
      <MenuButton
        aria-label={label}
        className="flex h-8 items-center gap-1 rounded px-2 text-sm text-n80 hover:bg-n20 focus:outline-none focus-visible:ring-2 focus-visible:ring-b50 data-[open]:bg-n20"
        data-testid="portal-language-switcher"
      >
        <FiGlobe aria-hidden className="size-4" />
        <span className="font-semibold uppercase">{current}</span>
        <FiChevronDown aria-hidden className="hidden size-4 sm:block" />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-popover mt-1 w-60 rounded border border-n30 bg-white py-1 shadow-shadow2 focus:outline-none"
      >
        <MenuSection>
          {LANGUAGES.map((language) => (
            <MenuItem key={language}>
              <button
                type="button"
                lang={language}
                onClick={() => portalLanguagePick.set(language)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-n90 data-[focus]:bg-n20"
              >
                {PORTAL_LANGUAGE_NAMES[language]}
                {language === current && (
                  <FiCheck aria-hidden className="size-4 text-b50" />
                )}
              </button>
            </MenuItem>
          ))}
          {account?._id && (
            <MenuHeading
              as="p"
              className="mt-1 border-t border-n30 px-3 pb-1 pt-2 text-xs text-n70"
            >
              {t("common:languageSwitcher:accountHint")}
            </MenuHeading>
          )}
        </MenuSection>
      </MenuItems>
    </Menu>
  );
}
